import React from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert';
import _set from 'lodash.set';
import { Field } from 'formik';
import AddressLookupField from 'shared/components/form/fields/address-lookup';
import RadioGroupField from 'shared/components/form/fields/radio-group';
import TextField from 'shared/components/form/fields/text';
import PhoneField from 'shared/components/form/fields/phone';
import SelectField from 'shared/components/form/fields/select';
import NpiField from 'shared/components/form/fields/npi';
import EmailVerifyField from 'shared/components/form/fields/email-verify';
import { States } from 'shared/utils/constants';
import apiClient from 'exogen-rx/utils/api-client';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { Redirect } from 'react-router-dom';
import { URLS } from 'bv360/utils/urls';
import ContactAdminModal from './contact-admin-modal';
import ModifyPracticeModal from './modify-practice-modal';
import PracticeFoundCard from './practice-found-card';

const stateOptions = States.map(state => ({ value: state.id, label: state.name }));

export const transformRadioValue = value => value === 'true';

export const radioOptions = [
  {
    label: 'Yes',
    value: true
  },
  {
    label: 'No',
    value: false
  }
];

class RegistrationForm extends React.PureComponent {
  state = {
    loading: false,
    error: null,
    cardError: null,
    redirectToContactSuccess: false,
    contactResult: null,
    showContactAdminModal: false,
    showPracticeModal: false,
    newPractice: null,
    offices: [],
    officesWithUsers: [],
    officeModalDismissed: false,
    providerNpiSearched: false,
    contactCardSubmitted: false,
    practiceFoundResolved: false
  };

  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps) {
    // Route typed registration errors to field-level errors when possible.
    if (prevProps.error === this.props.error) return;

    const err = this.props.error;
    if (!err || typeof err !== 'object') return;

    const code = err.code;
    const fieldErrors = err.fieldErrors;

    if (code === 'EXISTING_USER_EMAIL') {
      this.props.setFieldError(
        'practiceContact.email',
        'An account with this email already exists. Sign in or use a different email.'
      );
      return;
    }

    if (code === 'BIOVENTUS_EMPLOYEE_EMAIL') {
      this.props.setFieldError(
        'practiceContact.email',
        'Bioventus employees cannot self-register here.'
      );
      return;
    }

    if (code === 'EXISTING_NPI_ALT_CONFLICT') {
      this.props.setFieldError(
        'practice.npi',
        'This NPI is registered as an alternate identifier for another practice. If this is your practice, please call 800-836-4080.'
      );
      return;
    }

    if (code === 'VALIDATION_ERROR' && fieldErrors) {
      // fieldErrors may be nested (DRF style). We only set the handful of fields we care about
      // for this form to avoid brittle recursion.
      const practiceErrors = fieldErrors.practice || {};
      const providerErrors = fieldErrors.provider || {};
      const contactErrors = fieldErrors.practiceContact || {};

      if (practiceErrors.npi?.[0]) this.props.setFieldError('practice.npi', practiceErrors.npi[0]);
      if (practiceErrors.name?.[0]) this.props.setFieldError('practice.name', practiceErrors.name[0]);
      if (providerErrors.npi?.[0]) this.props.setFieldError('provider.npi', providerErrors.npi[0]);
      if (contactErrors.email?.[0])
        this.props.setFieldError('practiceContact.email', contactErrors.email[0]);
    }
  }

  updateOrIgnore = (field, value) => {
    if (!value) return;
    this.props.setFieldValue(field, value, true);
    const touched = _set(this.props.touched, field, true);
    this.props.setTouched(touched);
  };

  handlePracticeContactRegister = async () => {
    // Keep this simple: rely on EmailVerifyField and NpiField to do the checks.
    // We only allow advancing if required fields are filled, email has no field error,
    // and provider NPI lookup has completed (providerNpiUpdate ran).
    if (!this.hasPopulatedValidPracticeContact()) {
      this.setState({ cardError: 'Please fill in all required fields.' });
      return;
    }

    if (!this.state.providerNpiSearched) {
      this.setState({ cardError: 'Please enter a valid 10-digit Provider NPI.' });
      return;
    }

    this.setState({
      cardError: null,
      contactCardSubmitted: true,
      practiceFoundResolved: false
    });
  };

  handleKeyDown = evt => {
    if ((evt.charCode || evt.keyCode) !== 13) return;

    // Only handle Enter for the Practice Contact step.
    if (this.state.contactCardSubmitted) return;

    const name = evt?.target?.name || '';
    const isPracticeContactEnter =
      name === 'practiceContact.firstName' ||
      name === 'practiceContact.lastName' ||
      name === 'practiceContact.email' ||
      name === 'provider.npi';

    if (!isPracticeContactEnter) return;

    evt.preventDefault();
    this.handlePracticeContactRegister();
  };

  handleOfficeResult = (offices, officesWithUsers) => {
    const foundAny =
      (offices && offices.length > 0) || (officesWithUsers && officesWithUsers?.length > 0);

    if (foundAny) {
      this.setState({
        offices,
        officesWithUsers,
        officeModalDismissed: false
      });
    } else {
      this.setState({
        offices: [],
        officesWithUsers: [],
      });
      this.props.setFieldValue('newOffice', true);
    }

    const { provider, newOffice } = this.props.values;
    const { physicianExists } = provider;

    if (physicianExists && !newOffice) {
      this.props.setFieldValue('practice.office_id', '');
      this.props.setFieldValue('practice.npi', '');
      this.props.setFieldValue('practice.name', '');
      this.props.setFieldValue('practice.phone', '');
      this.props.setFieldValue('practice.fax', '');
      this.props.setFieldValue('practice.street', '');
      this.props.setFieldValue('practice.suite', '');
      this.props.setFieldValue('practice.state', '');
      this.props.setFieldValue('practice.city', '');
      this.props.setFieldValue('practice.zip', '');
      this.props.setFieldValue('practice.fax', '');
    }
  };

  handleContactAdmins = async office => {
    this.setState({ loading: true, error: null });
    const { values } = this.props;

    const { firstName, middleName = '', lastName, email } = values?.practiceContact;

    try {
      const response = await apiClient.post('exogen-rx/api/practices/join', {
        params: { practice: office?.practice?.id, firstName, lastName, email }
      });

      if (response?.body?.error) {
        this.setState({ error: response.body.error });
      } else {
        this.updateOrIgnore('practice.name', office?.practice?.name);
        this.updateOrIgnore('practice.npi', office?.practice?.npi);
        this.setState({
          loading: false,
          redirectToContactSuccess: true,
          contactResult: response?.body?.result,
          practiceAdmins: response?.body?.practice_admins
        });
      }
    } catch (e) {
      console.log(e);
      this.setState({ loading: false, error: e?.body?.error || 'Error requesting access' });
    }
  };

  updatePractice = ({ name, street, suite, state, city, zip, fax, phone }) => {
    this.updateOrIgnore('practice.office_id', '');
    this.updateOrIgnore('practice.name', name);
    this.updateOrIgnore('practice.phone', phone);
    this.updateOrIgnore('practice.fax', fax);
    this.updateOrIgnore('practice.street', street);
    this.updateOrIgnore('practice.suite', suite);
    this.updateOrIgnore('practice.state', state);
    this.updateOrIgnore('practice.city', city);
    this.updateOrIgnore('practice.zip', zip);
    this.setState({ showPracticeModal: false, newPractice: null });
  };

  practiceNpiUpdate = practice => {
    const { newOffice } = this.props.values;
    const { practiceExists, officesWithUsers } = practice;

    // If user dismissed the office modal but entered a Practice NPI that matches
    // one of the offices from the provider NPI lookup, re-show the office modal
    if (this.state.officeModalDismissed && practice.npi && this.practiceNpiMatchesOffice(practice.npi)) {
      return;
    }

    if (newOffice) {
      this.updatePractice(practice);
      this.setState({ newPractice: null });
    } else if (practice.name) {
      this.setState({ newPractice: { name: practice.name }, showPracticeModal: true });
    }

    if (practiceExists && officesWithUsers && officesWithUsers?.length > 0) {
      this.setState({
        officesWithUsers,
        showContactAdminModal: true
      });
    }
  };

  providerNpiUpdate = ({
    firstName,
    middleName,
    lastName,
    fax,
    phone,
    physicianExists,
    specialty,
    offices,
    officesWithUsers
  }) => {
    this.setState({ providerNpiSearched: true });
    this.handleOfficeResult(offices, officesWithUsers);
    this.props.setFieldValue('provider.physicianExists', physicianExists);
    const updateFunc =
      !firstName && !lastName && !specialty ? this.props.setFieldValue : this.updateOrIgnore;

    const { sameAsContact } = this.props.values.provider;

    if (!sameAsContact) {
      updateFunc('provider.firstName', firstName);
      updateFunc('provider.middleName', middleName);
      updateFunc('provider.lastName', lastName);
    }

    updateFunc('provider.specialty', specialty);
  };

  handleSameAsContact = isSame => {
    const { values } = this.props;
    if (!values.practiceContact) return null;
    const { physicianExists } = values;

    const { firstName, middleName = '', lastName, email } = values.practiceContact;
    const npi = values && values.npi ? values.npi : '';

    if (isSame && !physicianExists) {
      this.updateOrIgnore('provider.firstName', firstName);
      this.updateOrIgnore('provider.middleName', middleName);
      this.updateOrIgnore('provider.lastName', lastName);
      this.updateOrIgnore('provider.email', email);
      this.updateOrIgnore('provider.npi', npi);
    }
  };

  handleExistingOffice = ({
    id,
    name,
    practice,
    main_telephone,
    fax,
    address1,
    address2,
    city,
    state,
    zip
  }) => {
    this.updateOrIgnore('practice.office_id', id);
    this.updateOrIgnore('practice.name', name);
    if (practice?.npi) {
      this.updateOrIgnore('practice.npi', practice.npi);
    }
    this.updateOrIgnore('practice.phone', main_telephone);
    this.updateOrIgnore('practice.fax', fax);
    this.updateOrIgnore('practice.street', address1);
    this.updateOrIgnore('practice.suite', address2);
    this.updateOrIgnore('practice.state', state);
    this.updateOrIgnore('practice.city', city);
    this.updateOrIgnore('practice.zip', zip);
    this.setState({
      offices: [],
      officesWithUsers: [],
      practiceFoundResolved: true
    });
    this.props.setFieldValue('newOffice', false);
  };

  handleNewOffice = () => {
    this.props.setFieldValue('newOffice', true);
    this.setState({
      practiceFoundResolved: true
    });
    const { officeModalDismissed } = this.state;
    if (officeModalDismissed) {
      // Second dismissal — clear offices permanently
      this.setState({
        offices: [],
        officesWithUsers: [],
        officeModalDismissed: false
      });
    } else {
      // First dismissal — keep offices so we can re-show the modal
      this.setState({
        officeModalDismissed: true
      });
    }
  };

  practiceNpiMatchesOffice = (npi) => {
    const { offices, officesWithUsers } = this.state;
    const allOffices = [...(offices || []), ...(officesWithUsers || [])];
    return allOffices.some(o => o.practice?.npi === npi);
  };

  updateRecaptchaAndSubmit = async evt => {
    const recaptchaToken = await this.props.googleReCaptchaProps.executeRecaptcha('register_user');
    this.props.setFieldValue('recaptchaToken', recaptchaToken, true);
  };

  hasPopulatedValidPracticeContact = () => {
    const { values, errors: formErrors } = this.props;

    if (
      !values.practiceContact ||
      !values?.practiceContact?.firstName ||
      !values?.practiceContact?.lastName ||
      !values?.practiceContact?.email
    ) {
      return false;
    } else if (formErrors.practiceContact) {
      return false;
    } else {
      return true;
    }
  };

  render() {
    const { error, isValid, values } = this.props;
    const { loading, cardError } = this.state;

    const { practice, newOffice } = values;
    const { physicianExists, sameAsContact, inviteProvider, physicianIsUser } = values.provider;

    const {
      showContactAdminModal,
      showPracticeModal,
      newPractice,
      offices,
      officesWithUsers,
      redirectToContactSuccess,
      contactResult,
      practiceAdmins,
      contactCardSubmitted,
      practiceFoundResolved
    } = this.state;

    if (redirectToContactSuccess)
      return (
        <Redirect
          to={{
            pathname: URLS.ADMIN_CONTACTED,
            search: `?practice=${encodeURIComponent(values?.practice?.name || '')}&npi=${encodeURIComponent(
              values?.practice?.npi || 'name not known'
            )}&result=${encodeURIComponent(contactResult || '')}`,
            state: { practiceAdmins }
          }}
        />
      );

    const practiceWasFound =
      (offices && offices.length > 0) || (officesWithUsers && officesWithUsers?.length > 0);

    const shouldShowPracticeFoundCard =
      contactCardSubmitted && !practiceFoundResolved && practiceWasFound;

    const shouldShowProviderAndPracticeCards =
      contactCardSubmitted && (practiceFoundResolved || !practiceWasFound);

    return (
      <form
        onSubmit={async evt => {
          evt.preventDefault();
          await this.updateRecaptchaAndSubmit();
          this.props.handleSubmit(evt);
        }}
        onKeyDown={this.handleKeyDown}
        autoComplete="off"
        className="registration-form"
      >
        <Card>
          <Card.Header>Practice Contact</Card.Header>
          <Card.Body>
            <Row>
              <Col>
                Please input your contact information. You will be enrolled as the first practice
                user. Other users can be added later. (* are required)
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <Field
                  name="practiceContact.firstName"
                  label="First Name*"
                  maxLength={64}
                  component={TextField}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Field
                  name="practiceContact.lastName"
                  label="Last Name*"
                  maxLength={64}
                  component={TextField}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <Field
                  name="practiceContact.email"
                  label="Email* (Your Email Will be Your Username)"
                  maxLength={64}
                  component={EmailVerifyField}
                  verifyUrl="/mybv360/api/verify-user/contact/"
                  placeholder="Email*"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <Field
                  name="provider.npi"
                  label="Provider NPI*"
                  populateCallback={this.providerNpiUpdate}
                  enumerationType="NPI-1"
                  placeholder="NPI Number"
                  component={NpiField}
                  disabled={!this.hasPopulatedValidPracticeContact()}
                />
              </Col>
            </Row>
          </Card.Body>
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <div>
              {cardError && (
                <Alert variant="danger" className="mb-0 py-1 px-2">
                  {cardError}
                </Alert>
              )}
            </div>
            <Button
              type="button"
              variant="primary"
              disabled={loading}
              onClick={this.handlePracticeContactRegister}
            >
              Register
            </Button>
          </Card.Footer>
        </Card>

        {shouldShowPracticeFoundCard && (
          <PracticeFoundCard
            error={this.state.error}
            loading={loading}
            offices={offices}
            officesWithUsers={officesWithUsers}
            contactAdmin={this.handleContactAdmins}
            useExistingOffice={this.handleExistingOffice}
            createNewOffice={this.handleNewOffice}
          />
        )}

        {shouldShowProviderAndPracticeCards && (
          <>
            <Card>
              <Card.Header>Provider</Card.Header>
              <Card.Body>
                <Row>
                  <Col>
                    Please add one provider from the practice for identification purposes. Other
                    providers can be added later. (* are required)
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Field
                      name="provider.sameAsContact"
                      label="Is the contact above also the provider?*"
                      component={RadioGroupField}
                      elems={radioOptions}
                      onChange={this.handleSameAsContact}
                      disabled={!this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                {!sameAsContact && (
                  <Row>
                    <Col>
                      <Field
                        name="provider.inviteProvider"
                        label="Invite the physician to MyBV360?*"
                        component={RadioGroupField}
                        elems={radioOptions}
                        disabled={physicianIsUser || !this.hasPopulatedValidPracticeContact()}
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="provider.firstName"
                      label="First Name*"
                      maxLength={64}
                      component={TextField}
                      disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field
                      name="provider.middleName"
                      label="Middle Name"
                      maxLength={64}
                      component={TextField}
                      disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="provider.lastName"
                      label="Last Name*"
                      maxLength={64}
                      component={TextField}
                      disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field
                      label="DEA Number"
                      placeholder="AB1234567"
                      name="provider.dea"
                      component={TextField}
                      disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>

                <Row>
                  {inviteProvider && !sameAsContact && (
                    <Col xs={12} sm={6}>
                      <Field
                        label="Email*"
                        name="provider.email"
                        component={TextField}
                        disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                      />
                    </Col>
                  )}

                  <Col xs={12} sm={6}>
                    <Field
                      label="Specialty"
                      name="provider.specialty"
                      component={TextField}
                      disabled={physicianExists || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>Practice</Card.Header>
              <Card.Body>
                <Row>
                  <Col>Please input the name and location of your practice. (* are required)</Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.npi"
                      label="Practice NPI*"
                      populateCallback={this.practiceNpiUpdate}
                      enumerationType="NPI-2"
                      placeholder="NPI Number"
                      component={NpiField}
                      disabled={!this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.name"
                      label="Practice Name*"
                      placeholder="Practice Name"
                      maxLength={64}
                      component={TextField}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field name="practice.taxId" maxLength={9} label="Tax ID" component={TextField} />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.phone"
                      label="Phone Number*"
                      placeholder="(555) 555-5555"
                      showValidation
                      component={PhoneField}
                      disabled={!this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.phone_extension"
                      label="Phone Extension"
                      placeholder="12345"
                      showValidation
                      component={TextField}
                      maxLength={9}
                      disabled={!this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.fax"
                      label="Fax Number*"
                      placeholder="(555) 555-5555"
                      showValidation
                      component={PhoneField}
                      disabled={!this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.street"
                      aptField="practice.suite"
                      cityField="practice.city"
                      stateField="practice.state"
                      zipField="practice.zip"
                      showValidation
                      maxLength={64}
                      label="Street*"
                      component={AddressLookupField}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.suite"
                      maxLength={64}
                      label="Suite"
                      component={TextField}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.city"
                      maxLength={32}
                      label="City*"
                      component={TextField}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.state"
                      label="State*"
                      showValidation
                      simpleValue
                      component={SelectField}
                      options={stateOptions}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Field
                      name="practice.zip"
                      maxLength={5}
                      label="Zip*"
                      component={TextField}
                      disabled={!newOffice || !this.hasPopulatedValidPracticeContact()}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        )}

        <Row>
          <Col>
            {error && typeof error === 'object' && error.message?.trim() && (
              <Alert variant="danger" show={!!error}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />{' '}
                {error.message}
                {error.correlationId && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    Reference: {error.correlationId}
                  </div>
                )}
              </Alert>
            )}
          </Col>
        </Row>
        <div style={{ paddingLeft: '30px' }}>
          If assistance is needed, please call myBV360 Customer Service at{' '}
          <a href={'tel: 800-836-4080'}>800-836-4080</a>.
        </div>

        {shouldShowProviderAndPracticeCards && (
          <Row>
            <Col></Col>
            <Col md={{ span: 2, offset: 10 }}>
              <Button type="submit" disabled={!isValid || loading}>
                {/* Only render loading icon if loading is true */}
                {loading ? (
                  <FontAwesomeIcon
                    icon={faCircleNotch}
                    spin
                    aria-label="Loading"
                    style={{ marginRight: 4 }}
                  />
                ) : (
                  'Register'
                )}
              </Button>
            </Col>
          </Row>
        )}
        {showContactAdminModal && (
          <ContactAdminModal
            handleHide={() => this.setState({ showContactAdminModal: false })}
            loading={loading}
            offices={officesWithUsers}
            contactAdmin={this.handleContactAdmins}
            show={showContactAdminModal}
            error={this.state.error}
          />
        )}
        {this.props.showContactAdminModal && (
          <ContactAdminModal
            handleHide={() => this.props.handleRegistrationClose()}
            loading={loading}
            offices={this.props.officesWithUsers}
            contactAdmin={this.handleContactAdmins}
            show={this.props.showContactAdminModal}
            error={this.state.error}
          />
        )}
        {showPracticeModal && (
          <ModifyPracticeModal
            newPractice={newPractice}
            currentPractice={practice}
            close={() => this.updatePractice(practice)}
            updatePractice={() => this.updatePractice(newPractice)}
            show={showPracticeModal}
          />
        )}
      </form>
    );
  }
}

export default withGoogleReCaptcha(RegistrationForm);
